import Avo from '@susu/custom-made/Avo';
import type { typeCard } from '@susu/custom-made/components/card/card';
import { CardOrientation } from '@susu/custom-made/components/card/card';
import { ThemeContext } from '@susu/custom-made/providers';
import { Clients } from '@susu/custom-made/utils/constants';
import type {
  FabricGroup,
  MonogramColor,
  MonogramFontTypes,
  Option,
  typeFabricList,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import { getAvoEventParams, getConfigurationTypeText, onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { RefObject } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import styles from './cardList.module.scss';
import CardListBaseStyle from './cardListBaseStyle';
import CardListFabric from './cardListFabric';
import CardListMonogram from './cardListMonogram';
import CardListOption from './cardListOption';
import CardListInStore from './cardListInStore';
import { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';

export enum CardListType {
  Fabric,
  Option,
  Monogram,
}

export type cardItemsType =
  | FabricGroup[]
  | typeFabricList
  | Option[]
  | typeOptionValue
  | MonogramColor[]
  | MonogramFontTypes[];

export default function CardList({
  cardItems,
  cardSubItems,
  selectedItemId,
  cardOrientation,
  isGroup,
  isBaseStyleActive,
  cardListType,
  optionValues,
  showAdvancedButton,
  showRemoveAdditionalProductButton,
  testId,
  isPresentationOnly,
  isDetailedView,
  cardClick = () => {},
  infoIconClick,
  baseStyleClick,
  additionalProductRemoveClick,
  scanClick,
  searchClick,
  showAdvancedItems = false,
  section,
  selectedOption,
  cardItemRef,
  affectedConfigurationTypeId,
}: Readonly<{
  cardItems: cardItemsType;
  cardSubItems?: (string | number)[];
  selectedItemId?: number | null;
  cardOrientation?: CardOrientation;
  isGroup?: boolean;
  isBaseStyleActive?: boolean;
  cardListType: CardListType;
  optionValues?: typeOptionValue;
  showAdvancedButton?: boolean;
  showRemoveAdditionalProductButton?: boolean;
  isPresentationOnly?: boolean;
  isDetailedView?: boolean;
  cardClick?: (card: typeCard) => void;
  infoIconClick?: (card: typeCard) => void;
  baseStyleClick?: () => void;
  additionalProductRemoveClick?: () => void;
  scanClick?: () => void;
  searchClick?: () => void;
  testId?: string;
  showAdvancedItems?: boolean;
  section: string | null;
  selectedOption?: Option | null;
  cardItemRef?: RefObject<HTMLDivElement>;
  affectedConfigurationTypeId?: ConfigurationTypes;
}>) {
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState<boolean>(showAdvancedItems);
  const { state } = useContext(ThemeContext);
  const cardlistRef = useRef<HTMLInputElement>(null);

  const stylePresetSelectedValue = () => {
    const sectionConfigurationType = getConfigurationTypeBySection(section ?? '');

    return state.selectedBaseStyle[sectionConfigurationType]
      ? state.selectedBaseStyle[sectionConfigurationType].name
      : 'No selected preset';
  };

  const advanceClick = () => {
    setIsAdvancedExpanded(true);
    state.client === Clients.onLine && Avo.showAdvancedOptions(getAvoEventParams(state));
  };

  useEffect(() => {
    cardlistRef.current?.scrollTo(0, 0);
  }, [cardItems, cardSubItems]);

  return (
    <div
      className={cn(styles.cardListContainer, styles[cardOrientation ?? CardOrientation.Default], {
        [styles.isPresentationOnly]: isPresentationOnly,
      })}
      ref={cardlistRef}
      data-test-id={testId}
    >
      {showRemoveAdditionalProductButton && (
        <div className={styles.customCardContol}>
          <div
            data-test-id={`remove-${state.section}-btn-mobile`}
            onClick={additionalProductRemoveClick}
            onKeyDown={e => {
              onEnter(e, () => {
                additionalProductRemoveClick && additionalProductRemoveClick();
              });
            }}
            className={styles.customCardContolContainer}
          >
            <div className={styles.customCardContolIcon}>
              <i className="susu-icons" translate="no">
                none
              </i>
            </div>
          </div>
          <div className={styles.customCardContolText}>Remove</div>
          <div
            className={cn(styles.customCardContolTextDesktop, styles.removeCustomControlIcon)}
            data-test-id={`remove-${state.section}-btn-desktop`}
            onClick={additionalProductRemoveClick}
            onKeyDown={e => {
              onEnter(e, () => {
                additionalProductRemoveClick && additionalProductRemoveClick();
              });
            }}
          >
            Remove {getConfigurationTypeText(getConfigurationTypeBySection(state.section!))}
          </div>
        </div>
      )}
      {isBaseStyleActive && stylePresetSelectedValue() && (
        <CardListBaseStyle
          isPresentationOnly={isPresentationOnly}
          baseStyleClick={baseStyleClick}
          stylePresetSelectedValue={stylePresetSelectedValue}
        />
      )}
      {isBaseStyleActive && isPresentationOnly && <div className={styles.presentationTitle}>Design Options</div>}
      {state.client === Clients.inStore &&
        cardListType === CardListType.Fabric &&
        isGroup === true &&
        scanClick &&
        searchClick && <CardListInStore scanClick={scanClick} searchClick={searchClick} />}
      {cardListType === CardListType.Fabric && (
        <CardListFabric
          cardItems={cardItems}
          cardSubItems={cardSubItems}
          selectedItemId={selectedItemId}
          cardOrientation={cardOrientation}
          isGroup={isGroup}
          cardClick={cardClick}
          infoIconClick={infoIconClick}
        />
      )}
      {cardListType === CardListType.Option && (
        <CardListOption
          cardItems={cardItems}
          cardSubItems={cardSubItems}
          selectedItemId={selectedItemId}
          cardOrientation={cardOrientation}
          isGroup={isGroup}
          isAdvancedExpanded={isAdvancedExpanded}
          optionValues={optionValues}
          isPresentationOnly={isPresentationOnly}
          cardClick={cardClick}
          selectedOption={selectedOption}
          isDetailedView={isDetailedView}
          cardItemRef={cardItemRef}
          affectedConfigurationTypeId={affectedConfigurationTypeId}
        />
      )}
      {cardListType === CardListType.Monogram && (
        <CardListMonogram
          cardItems={cardItems}
          selectedItemId={selectedItemId}
          cardOrientation={cardOrientation}
          cardClick={cardClick}
        />
      )}
      {showAdvancedButton && !isAdvancedExpanded && (
        <div className={styles.customCardContol}>
          <div
            onClick={() => {
              setIsAdvancedExpanded(true);
            }}
            data-test-id="advanced-options-btn-mobile"
            onKeyDown={e => {
              onEnter(e, () => {
                setIsAdvancedExpanded(true);
              });
            }}
            className={styles.customCardContolContainer}
          >
            <div className={styles.customCardContolIcon}>
              <i className="susu-icons" translate="no">
                arrow_right
              </i>
            </div>
          </div>
          <div className={styles.customCardContolText}>Advanced</div>
          <div
            className={cn(styles.customCardContolTextDesktop, styles.advanceCustomControlIcon)}
            data-test-id="advanced-options-btn-desktop"
            onClick={advanceClick}
            onKeyDown={e => {
              onEnter(e, advanceClick);
            }}
          >
            Advanced options
          </div>
        </div>
      )}
    </div>
  );
}
